import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  FormLabel,
  FormHelperText,
  Stack,
  Tooltip,
  Grid,
} from "@mui/material";
import React, { useState } from "react";
import {
  useAddEntityIdentity,
  useRemoveEntityIdentity,
  useEntityIdentities,
} from "../hooks/useApiIdentities";
import { useAuthorizedEntities as useAuthorizedEntities } from "../hooks/useEntityQueries";
import { useZudokuTheme } from "../hooks/useZudokuTheme";
import { EntityIdentity } from "../services/apiIdentityService";
import {
  getMutedTextStyle,
  getTableDataCellStyle,
} from "../styling/fieldStyles";

// UUID validation function
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

interface EntityIdentitiesProps {
  onRefresh: () => void;
}

interface AddEntityDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (entity: EntityIdentity) => Promise<void>;
}

function AddEntityDialog({ open, onClose, onAdd }: AddEntityDialogProps) {
  const { currentTheme } = useZudokuTheme();
  const [label, setLabel] = useState("");
  const [entityId, setEntityId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      setError("Label is required");
      return;
    }
    if (!entityId.trim()) {
      setError("Entity ID is required");
      return;
    }
    if (!isValidUUID(entityId.trim())) {
      setError("Entity ID must be a valid UUID");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onAdd({
        id: entityId.trim(),
        label: label.trim(),
      });
      setLabel("");
      setEntityId("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add entity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setLabel("");
      setEntityId("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: currentTheme.card,
          color: currentTheme.foreground,
          border: `1px solid ${currentTheme.border}`,
        },
      }}
    >
      <DialogTitle>Add Entity Identity</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3}>
            <FormControl required>
              <FormLabel>Entity ID</FormLabel>
              <TextField
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                disabled={isSubmitting}
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Entity Label</FormLabel>
              <TextField
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. City of San Francisco"
                disabled={isSubmitting}
              />
              <FormHelperText>
                This will create a new entity identity that can be used for API
                authentication.
              </FormHelperText>
            </FormControl>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            !label.trim() ||
            !entityId.trim() ||
            !isValidUUID(entityId.trim())
          }
          variant="contained"
        >
          {isSubmitting ? "Adding..." : "Add Entity"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EntityIdentities({ onRefresh }: EntityIdentitiesProps) {
  const { currentTheme } = useZudokuTheme();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const { data: entities, isLoading, isError } = useEntityIdentities();
  const { mutateAsync: addEntity } = useAddEntityIdentity();
  const { mutateAsync: removeEntity } = useRemoveEntityIdentity();

  const mutedTextStyle = getMutedTextStyle(currentTheme);
  const tableDataStyle = getTableDataCellStyle(currentTheme);

  const handleAddEntity = async (entityData: EntityIdentity) => {
    await addEntity(entityData);
    onRefresh();
  };

  const handleDeleteEntity = async (entityId: string) => {
    setIsDeleting(entityId);
    try {
      await removeEntity(entityId);
      onRefresh();
    } catch (err) {
      console.error("Failed to delete entity:", err);
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !entities) {
    return <Alert severity="error">Failed to load entities</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Entity Identities
          </Typography>
          <Typography variant="body2" sx={mutedTextStyle}>
            Manage entity-specific identities for personal account
            authentication
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            fullWidth
          >
            Add Entity
          </Button>
        </Grid>
      </Grid>

      {entities.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: currentTheme.accent,
            border: `1px solid ${currentTheme.border}`,
            borderRadius: 1,
          }}
        >
          <BusinessIcon
            sx={{ fontSize: 48, color: currentTheme.mutedForeground, mb: 2 }}
          />
          <Typography
            variant="h4"
            sx={{
              ...mutedTextStyle,
              mb: 1,
            }}
          >
            No Entity Identities
          </Typography>
          <Typography variant="body2" sx={mutedTextStyle}>
            Add your first entity identity to get started
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entity</TableCell>
                <TableCell
                  sx={{
                    width: 60,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.map((entity) => {
                return (
                  <TableRow key={entity.id}>
                    <TableCell sx={tableDataStyle}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <BusinessIcon
                          sx={{ color: currentTheme.mutedForeground }}
                        />
                        <Box>
                          <Typography variant="body1">
                            {entity.label}
                          </Typography>
                          <Typography variant="body2" sx={mutedTextStyle}>
                            ID: {entity.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableDataStyle}>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDeleteEntity(entity.id)}
                          disabled={isDeleting === entity.id}
                          sx={{
                            color: "currentTheme.mutedForeground",
                            "&:hover": {
                              color: "error.main",
                            },
                          }}
                        >
                          {isDeleting === entity.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddEntityDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddEntity}
      />
    </Box>
  );
}

export function EntityIdentitiesTab({ onRefresh }: EntityIdentitiesProps) {
  const {
    data: authorizedEntities,
    isLoading,
    isError,
  } = useAuthorizedEntities();
  // Just list the entities the user has access to
  const { currentTheme } = useZudokuTheme();
  const mutedTextStyle = getMutedTextStyle(currentTheme);
  const tableDataStyle = getTableDataCellStyle(currentTheme);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !authorizedEntities) {
    return <Alert severity="error">Failed to load entities</Alert>;
  }

  const hasWildcard = authorizedEntities.meta.hasWildcard ?? false;

  if (hasWildcard) {
    return <EntityIdentities onRefresh={onRefresh} />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Entity Identities
          </Typography>
          <Typography variant="body2" sx={mutedTextStyle}>
            View entity-specific identities for personal account authentication
          </Typography>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {authorizedEntities.data.map((entity) => (
              <TableRow key={entity.id}>
                <TableCell sx={tableDataStyle}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BusinessIcon
                      sx={{ color: currentTheme.mutedForeground }}
                    />
                    <Box>
                      <Typography variant="body1">
                        {entity.attributes.displayName}
                      </Typography>
                      <Typography variant="body2" sx={mutedTextStyle}>
                        ID: {entity.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
